import React from "react";
import { Routes, Route, useParams, Navigate, useLocation } from "react-router-dom";
import AuthenticatedRoute from "../utils/AuthenticatedRoute/AuthenticatedRoute";
import Loadable from "react-loadable";
import DynamicImportLoader from "app/components/atoms/DynamicImportLoader/DynamicImportLoader";
import NotFoundPage from "app/containers/templates/NotFoundPage/NotFoundPage";
import MaintenancePage from "app/containers/templates/MaintenancePage/MaintenancePage";
import axios from "axios";
import { getDummyInteractiveList } from "app/assets/applications-hub/interactives/interactives-list";

//
// Helpers
// ------------------------------------------------------------------

const LandingPage = Loadable({
  loader: () =>
    import(/* webpackChunkName: "LandingPage" */ "../containers/templates/LandingPage/LandingPage"),
  loading: DynamicImportLoader,
  modules: ["LandingPage"]
});

const HubPage = Loadable({
  loader: () =>
    import(/* webpackChunkName: "Hub" */ "../containers/templates/HubPage/HubPage"),
  loading: DynamicImportLoader,
  modules: ["HubPage"]
});

const AdminToolsPage = Loadable.Map({
  loader: {
    AdminToolsPage: () =>
      import("../containers/templates/AdminToolsPage/AdminToolsPage")
  },
  loading: DynamicImportLoader,
  modules: ["AdminToolsPage"],
  render(loaded, props) {
    let AdminToolsPage = loaded.AdminToolsPage.default;
    return <AdminToolsPage {...props} />;
  }
});

const AdminToolPage = Loadable.Map({
  loader: {
    AdminToolPage: () =>
      import(/* webpackChunkName: "AdminToolPage" */ "app/containers/templates/AdminToolPage/AdminToolPage")
  },
  loading: () => null, // No loader here, because config tools each have their own
  modules: ["AdminToolPage"],
  render(loaded, props) {
    let AdminToolPage = loaded.AdminToolPage.default;
    
    // Create a wrapper component that will be rendered by React Router
    function AdminToolWithParams() {
      const params = useParams();
      return <AdminToolPage {...props} params={params} />;
    }
    
    return <AdminToolWithParams />;
  }
});

/**
 *  ---  Loading all applications and its groups ---
 */
const getAllInteractiveList = async () => {
  let result = { data: [], errors: [] };

  try {
    let appsRequest = await axios
      .get("/api/shared/get-applications")

      .then(res => {
        result.data = res.data;
        return result;
      })

      .catch(error => {
        if (error.response) {
          // Request made and server responded
          if (
            error.response.status === 404 &&
            process.env.NODE_ENV === "development"
          ) {
            const dummyResult = getDummyInteractiveList().then(res => {
              result.data = res;
              result.errors = ["Using dummy list. The list cannot be edited."];
              return result;
            });
            return dummyResult;
          } else {
            result.data = [];
            result.errors = []; 
            return result;
          }
        } else if (error.request) {
          // The request was made but no response was received
          console.log(error.request);
          result.data = [];
          result.errors = [
            "Did not receive any response from server. Try again later."
          ];
          return result;
        } else {
          // Something happened in setting up the request that triggered an Error
          console.log("Error", error.message);
          result.data = [];
          result.errors = ["Something went wrong. Try again later."];
          return result;
        }
      });

    return appsRequest;
  } catch (error) {
    console.log(error);

    result.data = [];
    result.errors = ["Something went wrong. Try again later."];
    return result;
  }
};

/**
 *  ---  We are loading all applications list from database here befoe the page loads. This is so to use application groups to control its visibility ---
 */
const InteractivesPage = Loadable.Map({
  loader: {
    InteractivesPage: () =>
      import("../containers/templates/InteractivesPage/InteractivesPage"),
    appItemsLists: () => {
      return getAllInteractiveList().then(d => d);
    }
  },
  loading: DynamicImportLoader,
  timeout: 10000, // 10 seconds
  modules: ["InteractivesPage"],
  render(loaded, props) {
    let InteractivesPage = loaded.InteractivesPage.default;
    let appItemsLists = loaded.appItemsLists;
    return <InteractivesPage {...props} appItemsLists={appItemsLists} />;
  }
});

const InteractiveConfigPage = Loadable.Map({
  loader: {
    InteractiveConfigPage: () =>
      import("../containers/templates/InteractiveConfigPage/InteractiveConfigPage"),
    appItemsLists: () => {
      return getAllInteractiveList().then(d => d);
    }
  },
  loading: () => null,
  modules: ["InteractiveConfigPage"],

  render(loaded, props) {
    let InteractiveConfigPage = loaded.InteractiveConfigPage.default;
    let appItemsLists = loaded.appItemsLists.data || [];
    
    // Create a wrapper component that will be rendered by React Router
    function InteractiveConfigWithParams() {
      const params = useParams();
      return <InteractiveConfigPage {...props} appItemsLists={appItemsLists} params={params} />;
    }
    
    return <InteractiveConfigWithParams />;
  }

  // render(loaded, props) {
  //   let InteractiveConfigPage = loaded.InteractiveConfigPage.default;
  //   let appItemsLists = loaded.appItemsLists.data;
  //   return <InteractiveConfigPage {...props} appItemsLists={appItemsLists} />;
  // }
});


const UserToolsPage = Loadable.Map({
  loader: {
    UserToolsPage: () =>
      import("../containers/templates/UserToolsPage/UserToolsPage")
  },
  loading: DynamicImportLoader,
  modules: ["UserToolsPage"],
  render(loaded, props) {
    let UserToolsPage = loaded.UserToolsPage.default;
    return <UserToolsPage {...props} />;
  }
});

const UserToolPage = Loadable.Map({
  loader: {
    UserToolPage: () =>
      import(/* webpackChunkName: "UserToolPage" */ "app/containers/templates/UserToolPage/UserToolPage")
  },
  loading: () => null, // No loader here, because config tools each have their own
  modules: ["UserToolPage"],
  render(loaded, props) {
    let UserToolPage = loaded.UserToolPage.default;
    
    // Create a wrapper component that will be rendered by React Router
    function UserToolWithParams() {
      const params = useParams();
      return <UserToolPage {...props} params={params} />;
    }
    
    return <UserToolWithParams />;
  }
});


//
// Routes
// ------------------------------------------------------------------

const AppRoutes = ({ customProp }) => {
  const [inMaintenance, setInMaintenance] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const location = useLocation();

  React.useEffect(() => {
    // Check maintenance status on mount
    const checkMaintenanceStatus = async () => {
      try {
        const response = await axios.get('/api/shared/get-maintenance-status');
        setInMaintenance(response.data.inMaintenance);
      } catch (error) {
        console.error('Error checking maintenance status:', error);
        setInMaintenance(false);
      } finally {
        setLoading(false);
      }
    };

    checkMaintenanceStatus();
  }, []);

  // Show loading state while checking maintenance status
  if (loading) {
    return null;
  }

  // If in maintenance mode and not already on maintenance page, redirect
  if (inMaintenance && location.pathname !== '/maintenance') {
    return <Navigate to="/maintenance" replace />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/hub" element={<AuthenticatedRoute component={HubPage} />} />
      <Route
        path="/admin-tools"
        element={<AuthenticatedRoute component={(props) => <AdminToolsPage {...props} customProp={customProp} />} />}
      /> 
      <Route
        path="/admin-tools/:id"
        element={
          <AuthenticatedRoute 
            component={(props) => {
              return <AdminToolPage {...props} />;
            }} 
          />
        }
      />
      <Route
        path="/interactives"
        element={<AuthenticatedRoute component={InteractivesPage} />}
      />
      <Route
        path="/interactives/:id"
        element={
          <AuthenticatedRoute 
            component={(props) => {
              // InteractiveConfigPage is already a component that uses useParams internally
              return <InteractiveConfigPage {...props} />;
            }} 
          />
        }
      />
      <Route
        path="/user-tools"
        element={<AuthenticatedRoute component={UserToolsPage} />}
      />
      <Route
        path="/user-tools/:id"
        element={<AuthenticatedRoute component={UserToolPage} />}
      />
      <Route 
        path="/maintenance" 
        element={inMaintenance ? <MaintenancePage /> : <Navigate to="/" replace />} 
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;